/**
 * Кадры дня с камер в отчёте смены (ПР-01, ВН-06).
 */
import { type DayFrame, VIDEO_EVENT_KINDS, type VideoEventKind } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '../../lib/api';
import { Panel } from '../../ui/layout';

function kindLabel(kind: string) {
  return VIDEO_EVENT_KINDS[kind as VideoEventKind] ?? kind;
}

function timeLabel(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

export function DayFramesPanel({ projectId, day }: { projectId: string; day: string }) {
  const q = useQuery({
    queryKey: ['site', 'day-frames', projectId, day],
    queryFn: () => api<DayFrame[]>(`/site/projects/${projectId}/day-frames`, { query: { date: day } }),
    enabled: Boolean(projectId && day),
    retry: false,
  });

  if (q.isError) return null;
  if (q.isLoading) {
    return (
      <Panel title="Кадры дня">
        <p className="text-sm text-ink-2">Загрузка кадров с камер…</p>
      </Panel>
    );
  }
  const frames = q.data ?? [];
  return (
    <Panel title="Кадры дня">
      <p className="mb-3 text-sm text-ink-2">
        Подтверждённые события и кадры прогресса с камер проекта за день отчёта.
      </p>
      {!frames.length ? (
        <p className="text-sm text-ink-2">За этот день кадров с камер нет.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {frames.map((f) => (
            <li key={f.eventId} className="flex gap-3 rounded-md border border-line bg-surface-2 p-3">
              <img
                src={`/api/v1/video/events/${f.eventId}/frame`}
                alt=""
                className="h-20 w-28 shrink-0 rounded object-cover bg-surface"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium text-ink">{kindLabel(f.kind)}</p>
                <p className="mt-1 break-words text-[13px] text-ink-2">
                  {f.cameraName} · {timeLabel(f.occurredAt)}
                </p>
                <Link
                  to="/video"
                  search={{ tab: 'events', project: projectId, event: f.eventId }}
                  className="mt-2 inline-flex min-h-11 items-center text-sm underline"
                >
                  Открыть в видеонаблюдении
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
